import { Create, Delete, Visibility, VisibilityOff } from '@mui/icons-material'
import ConfirmButton from 'components/ConfirmButton'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import React, { useCallback } from 'react'
import { graphql, useFragment } from 'react-relay'
import { browserHistory } from 'services/router'
import { BlogPostListItem_UpdateMutation } from '__generated__/BlogPostListItem_UpdateMutation.graphql'
import { BlogPostListItem_DeleteMutation } from '__generated__/BlogPostListItem_DeleteMutation.graphql'
import BlogPostItem from 'scenes/Blog/components/BlogPostItem'
import { BlogPostListItem_BlogPost$key } from '__generated__/BlogPostListItem_BlogPost.graphql'
import { Nullable } from 'types'

const handleEditClick = (slug: Nullable<string>, userSlug: Nullable<String>) => {
  if (slug) {
    browserHistory.push(`/blog/${userSlug}/[post]/edit`, `/blog/${userSlug}/${slug}/edit`)
  }
}

export interface IBlogPostListItemProps {
  blogPostFragmentRef: BlogPostListItem_BlogPost$key
  editable: boolean
}

export const BlogPostListItem = (props: IBlogPostListItemProps) => {
  const { blogPostFragmentRef, editable } = props
  const blogPost = useFragment(graphql`
    fragment BlogPostListItem_BlogPost on BlogPost {
      status
      excerpt
      slug
      id
      user {
        profile {
          slug
        }
      }
      ...BlogPostItem_BlogPost
    }
  `, blogPostFragmentRef)

  const isPublished = blogPost?.status === 'published' || blogPost?.status === 'approved'

  const updateBlogPost = useQuickCommit<BlogPostListItem_UpdateMutation>(graphql`
    mutation BlogPostListItem_UpdateMutation ($input: UpdateBlogPostInput!) {
      updateBlogPost(input: $input){
        blogPost {
          status
          ...BlogPostListItem_BlogPost
        }
      }
    }
  `)

  const deleteBlogPost = useQuickCommit<BlogPostListItem_DeleteMutation>(graphql`
    mutation BlogPostListItem_DeleteMutation ($input: DeleteBlogPostInput!) {
      deleteBlogPost(input: $input){
        blogPost {
          id @deleteRecord
        }
      }
    }
  `)

  const { showSnackbarMessage } = useSnackbar()

  const handleVisibilityToggleClick = useCallback(async () => {
    const publishedValue = !isPublished
    const result = await updateBlogPost.execute({
      input: {
        blogPostId: blogPost.id,
        published: publishedValue,
      },
    })
    const updatedStatus = result?.updateBlogPost?.blogPost?.status
    if ((publishedValue && updatedStatus === 'published') || (!publishedValue && updatedStatus !== 'published')) {
      showSnackbarMessage('Blog post updated', { level: 'success' })
    }
  }, [updateBlogPost.execute, blogPost, isPublished])

  const handleDeleteClick = useCallback(async () => {
    const result = await deleteBlogPost.execute({
      input: {
        blogPostId: blogPost.id,
      },
    })
    if (result?.deleteBlogPost?.blogPost?.id) {
      showSnackbarMessage('Blog post deleted', { level: 'success' })
    }
  }, [deleteBlogPost?.execute, blogPost?.id])

  if (!blogPost) {
    return null
  }

  let excerpt = blogPost.excerpt

  if (excerpt?.length && excerpt.length > 100) {
    excerpt = excerpt.substring(0, 100)
  }

  return (
    <BlogPostItem
      blogPostFragmentRef={blogPost}
      variant="outlined"
      action={editable && (
        <React.Fragment>
          <ConfirmButton
            icon
            tooltip={isPublished ? 'Post is public - click to revert it to private draft' : 'Post is private draft - click to publish it'}
            dialogTitle="Toggle visibility"
            dialogMessage={isPublished ? 'This will hide your post from the public. Continue?' : 'This will publish your post to the public. Continue?'}
            onClick={() => handleVisibilityToggleClick()}
            data-cy="toggle-visibility"
          >
            {isPublished ? <Visibility /> : <VisibilityOff />}
          </ConfirmButton>

          <ConfirmButton
            icon
            tooltip="Edit blog post"
            dialogTitle="Edit blog post"
            dialogMessage="You will now be redirected to the blog post editor page. Unsaved changes to the profile will be lost. Continue?"
            onClick={() => handleEditClick(blogPost?.slug, blogPost?.user?.profile?.slug)}
            data-cy="edit-post"
          >
            <Create />
          </ConfirmButton>

          <ConfirmButton
            icon
            tooltip="Delete blog post"
            dialogTitle="Delete blog post"
            dialogMessage="Delete this blog post? This cannot be undone."
            onClick={() => handleDeleteClick()}
            critical
          >
            <Delete />
          </ConfirmButton>
        </React.Fragment>
      )}
    />
  )
}
